// src/components/common/Header.js
import {
  // Añadir esta línea para el nuevo ícono
  FileCopy as ArticleIcon,
  Article as BlogIcon,
  Brightness4,
  Brightness7,
  Chat as ChatIcon,
  Close as CloseIcon,
  ContactSupport as ContactIcon,
  Forum as ForumIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Login as LoginIcon,
  Logout as LogoutIcon, // Añadir esta línea
  Menu as MenuIcon,
  Notifications as NotificationIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  MedicalServices as SpecialistsIcon,
} from "@mui/icons-material";
import {
  Alert,
  AppBar,
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Slide,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useScrollTrigger,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { memo, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import "../../styles/components/Header.css";
// Añadir esta importación junto a las demás importaciones de Firebase
import { signOut } from "firebase/auth";
import { auth } from "../../services/firebase";

// Constantes para facilitar el mantenimiento
const NAV_ITEMS = [
  { key: "home", icon: <HomeIcon fontSize="small" /> },
  { key: "blog", icon: <BlogIcon fontSize="small" /> },
  { key: "info", icon: <InfoIcon fontSize="small" /> },
  { key: "resources-art", icon: <ArticleIcon fontSize="small" /> },
  { key: "chatbot", icon: <ChatIcon fontSize="small" /> },
  { key: "forum", icon: <ForumIcon fontSize="small" /> },
  { key: "specialists", icon: <SpecialistsIcon fontSize="small" /> },
  { key: "contact", icon: <ContactIcon fontSize="small" /> },
];
const SUPPORTED_LANGUAGES = ["en", "es", "pt", "fr", "de", "it"];

// Componente de botón de navegación mejorado
const NavButton = styled(Button)(({ theme, active }) => ({
  position: "relative",
  color: "#FFFFFF",
  textTransform: "none",
  fontSize: "0.9rem", // Reducir de 1rem
  fontWeight: 600,
  padding: "6px 12px", // Reducir de 8px 16px
  borderRadius: "8px",
  transition: "all 0.3s ease",
  backgroundColor: active ? theme.palette.primary.dark : "transparent",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  "&:after": {
    content: '""',
    position: "absolute",
    width: active ? "100%" : "0",
    height: "3px",
    bottom: "-4px",
    left: active ? "0" : "50%",
    backgroundColor: theme.palette.secondary.main,
    transition: "all 0.3s ease-in-out",
  },
  "&:hover:after": {
    width: "100%",
    left: "0",
  },
  "&:focus": {
    outline: `2px solid ${theme.palette.secondary.light}`,
    outlineOffset: "2px",
  },
}));

// Botón de acción secundaria
const ActionButton = styled(Button)(({ theme }) => ({
  color: "#FFFFFF",
  fontSize: "0.9rem",
  fontWeight: 600,
  borderRadius: "20px",
  padding: "6px 16px",
  backgroundColor: "rgba(255, 255, 255, 0.15)",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.25)",
    transform: "translateY(-2px)",
  },
}));

// Componente memoizado para botones de navegación
const MemoNavButton = memo(
  ({ item, currentPath, handleChatOpen, ariaLabel }) => {
    const { t } = useTranslation();

    // Determinar si este botón debe ser un link o un botón normal
    const isButton = item.key === "chatbot";
    const isActive = currentPath === item.key;
    const path = item.key === "home" ? "/" : `/${item.key}`;

    return (
      <Tooltip
        title={
          isActive
            ? t("header.currentPage", { page: t(`header.nav.${item.key}`) })
            : ""
        }
        arrow
      >
        <NavButton
          component={isButton ? "button" : RouterLink}
          to={!isButton ? path : undefined}
          onClick={isButton ? handleChatOpen : undefined}
          active={isActive ? 1 : 0}
          aria-current={isActive ? "page" : undefined}
          aria-label={ariaLabel || t(`header.nav.${item.key}`)}
          startIcon={item.icon}
        >
          {t(`header.nav.${item.key}`) || item.key}
        </NavButton>
      </Tooltip>
    );
  },
);
MemoNavButton.displayName = "MemoNavButton";

// Componente memoizado para el selector de idioma
const LanguageSelector = memo(
  ({ currentLang, onLanguageChange, size = "medium" }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [showNotice, setShowNotice] = useState(false);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleLanguageSelect = (lang) => {
      // Solo permitir español por ahora, mostrar aviso para otros idiomas
      if (lang === "es") {
        onLanguageChange(lang);
        handleMenuClose();
      } else {
        // Mostrar aviso temporal
        setShowNotice(true);
        handleMenuClose();
      }
    };

    return (
      <>
        <Button
          id="language-button"
          aria-controls={open ? "language-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleMenuOpen}
          size={size}
          sx={{
            color: "#FFFFFF",
            fontSize: size === "small" ? "0.8rem" : "0.9rem",
            fontWeight: 600,
            textTransform: "uppercase",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            minWidth: "48px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
          aria-label={t("header.selectLanguage") || "Seleccionar idioma"}
        >
          {currentLang?.substring(0, 2).toUpperCase() || "ES"}
        </Button>

        <Menu
          id="language-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          MenuListProps={{
            "aria-labelledby": "language-button",
          }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <MenuItem
              key={lang}
              onClick={() => handleLanguageSelect(lang)}
              selected={currentLang?.startsWith(lang)}
              aria-label={t(`languages.${lang}`) || lang}
              sx={lang !== "es" ? { opacity: 0.7 } : {}}
            >
              {t(`languages.${lang}`) || lang}
              {lang !== "es" && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  (Próximamente)
                </Typography>
              )}
            </MenuItem>
          ))}
        </Menu>

        {/* Snackbar con el aviso */}
        <Snackbar
          open={showNotice}
          autoHideDuration={4000}
          onClose={() => setShowNotice(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowNotice(false)}
            severity="info"
            sx={{
              width: "100%",
              boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
              bgcolor: (theme) => theme.palette.primary.light,
              color: "#fff",
            }}
          >
            {t(
              "header.comingSoonLanguages",
              "Próximamente más idiomas. Estamos trabajando en ello ❤️",
            )}
          </Alert>
        </Snackbar>
      </>
    );
  },
);
LanguageSelector.displayName = "LanguageSelector";

const Header = ({
  toggleTheme,
  mode,
  showTitle = true,
  onChatOpen,
  logoSrc = "/assets/logo.png",
  user,
}) => {
  const { t, i18n } = useTranslation();
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 50 });
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Estados
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [showMobileLanguageNotice, setShowMobileLanguageNotice] =
    useState(false);
  const headerRef = useRef(null);
  const [announceMessage, setAnnounceMessage] = useState("");

  // 1. MOVER TODAS LAS FUNCIONES CON HOOKS AL NIVEL SUPERIOR DEL COMPONENTE

  // Para anunciar cambios a lectores de pantalla
  const announceToScreenReader = useCallback((message) => {
    setAnnounceMessage(message);
    // Limpiar el mensaje después de que se haya leído
    setTimeout(() => setAnnounceMessage(""), 1000);
  }, []);

  // Manejadores de eventos - memoizados para mejor rendimiento
  const handleLanguageChange = useCallback(
    (lang) => {
      i18n.changeLanguage(lang);
      announceToScreenReader(
        t("header.languageChanged", { language: t(`languages.${lang}`) }),
      );
    },
    [i18n, t, announceToScreenReader],
  );

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleChatButtonClick = useCallback(() => {
    if (onChatOpen && typeof onChatOpen === "function") {
      onChatOpen();
    }

    if (isMobile) {
      handleMobileMenuClose();
    }

    announceToScreenReader(t("header.chatbotOpened") || "Chatbot abierto");
  }, [onChatOpen, isMobile, handleMobileMenuClose, t, announceToScreenReader]);

  // Manejar el error de carga del logo
  const handleLogoError = useCallback(() => {
    setLogoError(true);
  }, []);

  // Determinar la ruta activa
  const getPathName = useCallback(() => {
    const path = location.pathname;
    if (path === "/") return "home";
    return path.split("/")[1];
  }, [location.pathname]);

  const currentPath = getPathName();

  // Obtener iniciales para avatar fallback
  const getInitials = useCallback(() => {
    if (user && user.displayName) {
      return user.displayName
        .split(" ")
        .map((word) => word[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    }
    return "VD";
  }, [user]);

  // Las funciones que no usan hooks pueden seguir como funciones regulares
  const renderLogo = () => {
    if (logoError) {
      return (
        <Box
          sx={{
            width: { xs: 60, sm: 80 },
            height: { xs: 60, sm: 80 },
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.mode === "dark" ? "#102a43" : "#1a365d",
            color: "#FFFFFF",
            fontWeight: "bold",
            fontSize: { xs: "1.5rem", sm: "1.8rem" },
            boxShadow: "0 8px 16px rgba(0,0,0,0.5)",
          }}
          role="img"
          aria-label={t("header.logoAlt") || "Logo de VicDown"}
        >
          {getInitials()}
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={logoSrc}
        alt={t("header.logoAlt") || "Logo de VicDown"}
        onError={handleLogoError}
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          borderRadius: "10px",
          display: "block",
          transition: "transform 0.3s",
          boxShadow: "0 8px 16px rgba(0,0,0,0.5)",
          filter: "contrast(1.25) brightness(1.1)",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      />
    );
  };

  // Renderizar la navegación de escritorio
  const renderDesktopNav = () => (
    <>
      {/* Skip link para accesibilidad — WCAG AAA contraste 8.5:1 */}
      <Box
        component="a"
        href="#main-content"
        className="skip-link"
        sx={{
          position: "absolute",
          top: "-40px",
          left: 0,
          background: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
          padding: "8px 16px",
          zIndex: 9999,
          textDecoration: "underline",
          fontWeight: 600,
          borderRadius: "0 0 4px 0",
          transition: "top 0.2s",
          "&:focus": {
            top: 0,
            outline: `3px solid ${theme.palette.secondary.main}`,
            outlineOffset: "2px",
          },
        }}
      >
        {t("header.skipToContent") || "Saltar al contenido principal"}
      </Box>

      {/* Navegación */}
      <Box
        component="nav"
        aria-label={t("header.mainNavigation") || "Navegación principal"}
        sx={{
          // 1. Mostrar como GRID en 'md' y 'lg', ocultar debajo ('xs', 'sm')
          display: { xs: "none", md: "grid" },

          // 2. Definir 4 columnas. 'minmax' da flexibilidad controlada al ancho.
          //    Cada columna será al menos tan ancha como su contenido mínimo,
          //    pero no más ancha que su contenido máximo. Esto debería evitar solapamientos.
          gridTemplateColumns: "repeat(4, minmax(min-content, max-content))",

          // 3. Espacio entre filas y columnas
          gap: theme.spacing(1.5), // Ajusta el espacio si es necesario

          // 4. Centrar los botones DENTRO de sus celdas asignadas
          justifyItems: "center",

          // 5. Centrar el GRID COMPLETO horizontalmente en el espacio disponible
          mx: "auto",

          // 6. (Opcional, como precaución extra) Asegurar que el contenedor no permita desbordamiento visual
          // overflow: 'hidden',
        }}
      >
        {NAV_ITEMS.map((item) => (
          // El MemoNavButton se renderiza aquí como antes
          <MemoNavButton
            key={item.key}
            item={item}
            currentPath={currentPath}
            handleChatOpen={handleChatButtonClick}
            ariaLabel={
              t(`header.a11y.${item.key}`) ||
              `Ir a ${t(`header.nav.${item.key}`) || item.key}`
            }
          />
        ))}
      </Box>

      {/* Botones de acción */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {user ? (
          <>
            <Tooltip title={t("header.viewProfile") || "Ver tu perfil"} arrow>
              <ActionButton
                component={RouterLink}
                to="/profile"
                startIcon={<PersonIcon />}
                sx={{ minWidth: "100px" }}
                aria-label={t("header.a11y.profile") || "Ver tu perfil"}
              >
                {t("header.profile") || "Perfil"}
              </ActionButton>
            </Tooltip>

            {/* Botón de cerrar sesión */}
            <Tooltip title={t("header.logout") || "Cerrar sesión"} arrow>
              <ActionButton
                onClick={() => {
                  signOut(auth);
                  announceToScreenReader(
                    t("header.loggedOut") || "Has cerrado sesión",
                  );
                }}
                startIcon={<LogoutIcon />}
                sx={{
                  minWidth: "100px",
                  backgroundColor: "rgba(211, 47, 47, 0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.25)",
                  },
                }}
                aria-label={t("header.a11y.logout") || "Cerrar sesión"}
              >
                {t("header.logout") || "Salir"}
              </ActionButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip
              title={t("header.loginTooltip") || "Iniciar sesión en tu cuenta"}
              arrow
            >
              <ActionButton
                component={RouterLink}
                to="/login"
                startIcon={<LoginIcon />}
                sx={{ minWidth: "100px", mr: 1 }}
                aria-label={t("header.a11y.login") || "Iniciar sesión"}
              >
                {t("header.login") || "Iniciar sesión"}
              </ActionButton>
            </Tooltip>

            <Tooltip
              title={t("header.registerTooltip") || "Crear una nueva cuenta"}
              arrow
            >
              <ActionButton
                component={RouterLink}
                to="/register"
                startIcon={<PersonAddIcon />}
                sx={{
                  minWidth: "100px",
                  mr: 1,
                  backgroundColor: "rgba(233, 30, 99, 0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(233, 30, 99, 0.25)",
                    transform: "translateY(-2px)",
                  },
                }}
                aria-label={t("header.a11y.register") || "Registrarse"}
              >
                {t("header.register") || "Registrarse"}
              </ActionButton>
            </Tooltip>
          </>
        )}

        {/* Botón de suscripción */}
        <Tooltip
          title={t("header.subscribeTooltip") || "Ver opciones de suscripción"}
          arrow
        >
          <ActionButton
            component={RouterLink}
            to="/subscriptions"
            startIcon={
              <Badge color="error" variant="dot" invisible={false}>
                <NotificationIcon fontSize="small" />
              </Badge>
            }
            aria-label={t("header.a11y.subscribe") || "Ver suscripciones"}
          >
            {t("header.subscribe") || "Suscribirse"}
          </ActionButton>
        </Tooltip>

        {/* Selector de idioma */}
        <LanguageSelector
          currentLang={i18n.language}
          onLanguageChange={handleLanguageChange}
        />

        {/* Interruptor de modo claro/oscuro */}
        <Tooltip
          title={
            t(mode === "light" ? "header.darkMode" : "header.lightMode") ||
            (mode === "light"
              ? "Cambiar a modo oscuro"
              : "Cambiar a modo claro")
          }
          arrow
        >
          <IconButton
            onClick={toggleTheme}
            aria-label={
              t(
                mode === "light"
                  ? "header.a11y.darkMode"
                  : "header.a11y.lightMode",
              ) ||
              (mode === "light" ? "Activar modo oscuro" : "Activar modo claro")
            }
            size="medium"
            sx={{
              color: "#FFFFFF",
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              width: "40px",
              height: "40px",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                color: theme.palette.secondary.main,
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {mode === "light" ? <Brightness4 /> : <Brightness7 />}
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );

  // Reemplazar el renderMobileButton actual (línea ~551)
  const renderMobileButton = () => (
    <IconButton
      aria-label={
        mobileMenuOpen
          ? t("header.closeMenu") || "Cerrar menú"
          : t("header.openMenu") || "Abrir menú"
      }
      onClick={handleMobileMenuToggle}
      sx={{
        display: { xs: "flex", md: "none" },
        color: "#FFFFFF",
        ml: "auto",
        // Mejoras de accesibilidad
        padding: "12px", // Mayor área táctil
        backgroundColor: mobileMenuOpen
          ? "rgba(255,255,255,0.2)"
          : "rgba(255,255,255,0.1)",
        width: "48px", // Tamaño mínimo recomendado para accesibilidad
        height: "48px",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.3)",
          transform: "scale(1.05)",
        },
        transition: "all 0.2s ease-out",
      }}
    >
      {mobileMenuOpen ? (
        <CloseIcon fontSize="medium" />
      ) : (
        <MenuIcon fontSize="medium" />
      )}
    </IconButton>
  );

  // Renderizar el menú móvil
  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenuClose}
      transitionDuration={{ enter: 250, exit: 200 }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: 250,
          backgroundColor:
            mode === "dark"
              ? "rgba(18, 25, 38, 0.97)" // Color oscuro para modo oscuro
              : theme.palette.primary.main, // Color actual para modo claro
          color: "#FFFFFF",
          boxShadow:
            mode === "dark"
              ? "-4px 0px 15px rgba(0,0,0,0.4)"
              : "-4px 0px 15px rgba(0,0,0,0.25)",
          transition:
            "background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        },
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t("header.mobileMenu") || "Menú de navegación"}
    >
      <ClickAwayListener onClickAway={handleMobileMenuClose}>
        <Box>
          <Box
            sx={{ p: 2 }}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("header.menu") || "Menú"}
            </Typography>
            <IconButton
              onClick={handleMobileMenuClose}
              sx={{ color: "#FFFFFF" }}
              aria-label={t("header.closeMenu") || "Cerrar menú"}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider
            sx={{
              borderColor:
                mode === "dark"
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(255,255,255,0.2)",
            }}
          />

          <List
            component="nav"
            aria-label={t("header.mainNavigation") || "Navegación principal"}
          >
            {NAV_ITEMS.map((item) => (
              <ListItem
                key={item.key}
                component={item.key === "chatbot" ? "button" : RouterLink}
                to={
                  item.key === "chatbot"
                    ? undefined
                    : item.key === "home"
                      ? "/"
                      : `/${item.key}`
                }
                button
                selected={currentPath === item.key}
                onClick={
                  item.key === "chatbot"
                    ? handleChatButtonClick
                    : handleMobileMenuClose
                }
                sx={{
                  color: "#FFFFFF",
                  padding: "12px 16px", // Aumentar padding vertical
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.dark,
                    borderLeft: `4px solid ${theme.palette.secondary.main}`,
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "& .MuiListItemText-primary": {
                    // Aumentar tamaño de texto
                    fontSize: "1.05rem",
                    fontWeight: currentPath === item.key ? 700 : 500,
                  },
                  marginBottom: "2px", // Espacio entre elementos
                }}
                role={item.key === "chatbot" ? "button" : undefined}
                aria-current={currentPath === item.key ? "page" : undefined}
              >
                <ListItemIcon sx={{ color: "#FFFFFF" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={t(`header.nav.${item.key}`) || item.key}
                  primaryTypographyProps={{
                    fontWeight: currentPath === item.key ? 600 : 400,
                  }}
                />
              </ListItem>
            ))}

            <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.2)" }} />

            {/* Verificación de usuario para el menú móvil */}
            {user ? (
              <>
                {/* Botón de perfil para usuarios autenticados */}
                <ListItem
                  button
                  component={RouterLink}
                  to="/profile"
                  onClick={handleMobileMenuClose}
                >
                  <ListItemIcon sx={{ color: "#FFFFFF" }}>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary={t("header.profile") || "Perfil"} />
                </ListItem>

                {/* Botón de cerrar sesión en menú móvil */}
                <ListItem
                  button
                  onClick={() => {
                    signOut(auth);
                    handleMobileMenuClose();
                    announceToScreenReader(
                      t("header.loggedOut") || "Has cerrado sesión",
                    );
                  }}
                >
                  <ListItemIcon sx={{ color: "#FFFFFF" }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("header.logout") || "Cerrar sesión"}
                  />
                </ListItem>
              </>
            ) : (
              // Botones de login y registro para usuarios no autenticados
              <>
                {/* Login en menú móvil */}
                <ListItem
                  button
                  component={RouterLink}
                  to="/login"
                  onClick={handleMobileMenuClose}
                >
                  <ListItemIcon sx={{ color: "#FFFFFF" }}>
                    <LoginIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("header.login") || "Iniciar sesión"}
                  />
                </ListItem>

                {/* Registro en menú móvil */}
                <ListItem
                  button
                  component={RouterLink}
                  to="/register"
                  onClick={handleMobileMenuClose}
                >
                  <ListItemIcon sx={{ color: "#FFFFFF" }}>
                    <PersonAddIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("header.register") || "Registrarse"}
                  />
                </ListItem>
              </>
            )}

            {/* Suscripción en menú móvil */}
            <ListItem
              button
              component={RouterLink}
              to="/subscriptions"
              onClick={handleMobileMenuClose}
            >
              <ListItemIcon sx={{ color: "#FFFFFF" }}>
                <Badge color="error" variant="dot" invisible={false}>
                  <NotificationIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText primary={t("header.subscribe") || "Suscribirse"} />
            </ListItem>

            <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.2)" }} />

            {/* Selector de idioma en menú móvil */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor:
                  mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)",
                mb: 1,
              }}
            >
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                {t("header.selectLanguage") || "Seleccionar idioma"}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <Button
                    key={lang}
                    variant={
                      i18n.language.startsWith(lang) ? "contained" : "outlined"
                    }
                    size="small"
                    onClick={() => {
                      if (lang === "es") {
                        handleLanguageChange(lang);
                        handleMobileMenuClose();
                      } else {
                        // Mostrar aviso temporal
                        setShowMobileLanguageNotice(true);
                      }
                    }}
                    sx={{
                      minWidth: "48px",
                      color: i18n.language.startsWith(lang)
                        ? "#FFFFFF"
                        : theme.palette.secondary.light,
                      borderColor: theme.palette.secondary.light,
                      backgroundColor: i18n.language.startsWith(lang)
                        ? theme.palette.secondary.main
                        : "transparent",
                      opacity: lang !== "es" ? 0.7 : 1,
                    }}
                  >
                    {lang.toUpperCase()}
                    {lang !== "es" && (
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          fontSize: "10px",
                        }}
                      >
                        ★
                      </Typography>
                    )}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Toggle de tema en menú móvil */}
            <ListItem
              button
              onClick={() => {
                toggleTheme(); // Mantener solo esta llamada
                // Eliminar el handleMobileMenuClose() que agregué incorrectamente
              }}
            >
              <ListItemIcon sx={{ color: "#FFFFFF" }}>
                {mode === "light" ? <Brightness4 /> : <Brightness7 />}
              </ListItemIcon>
              <ListItemText
                primary={
                  t(
                    mode === "light" ? "header.darkMode" : "header.lightMode",
                  ) || (mode === "light" ? "Modo oscuro" : "Modo claro")
                }
              />
            </ListItem>
          </List>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
              mb: 2,
              px: 2,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleMobileMenuClose}
              startIcon={<CloseIcon />}
              sx={{
                color: "#FFFFFF",
                borderColor: "rgba(255,255,255,0.5)",
                padding: "10px",
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderColor: "#FFFFFF",
                },
              }}
            >
              {t("header.closeMenu") || "Cerrar menú"}
            </Button>
          </Box>
        </Box>
      </ClickAwayListener>
    </Drawer>
  );

  return (
    <>
      {/* Anunciador para lectores de pantalla */}
      {announceMessage && (
        <div
          aria-live="polite"
          className="sr-only"
          style={{
            position: "absolute",
            height: 1,
            width: 1,
            padding: 0,
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {announceMessage}
        </div>
      )}

      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar
          ref={headerRef}
          className="header"
          position="sticky"
          sx={{
            backgroundColor:
              mode === "dark"
                ? "rgba(18, 25, 38, 0.97)" // Color oscuro para modo oscuro
                : theme.palette.primary.main, // Color actual para modo claro
            boxShadow:
              mode === "dark"
                ? "0 2px 10px rgba(0,0,0,0.4)"
                : "0 2px 10px rgba(0,0,0,0.2)",
            transition:
              "background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
          }}
          component="header"
          role="banner"
        >
          <Toolbar
            sx={{
              justifyContent: "space-between",
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 0.5, sm: 1 },
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Logo y título */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                textDecoration: "none",
              }}
              aria-label={t("header.goToHome") || "Ir a la página principal"}
            >
              {renderLogo()}
              {showTitle && (
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    color: "#FFFFFF",
                    fontFamily: "'Comfortaa', 'Quicksand', sans-serif",
                    fontSize: { xs: "1.2rem", sm: "1.5rem" },
                    fontWeight: 600,
                    ml: 1,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(45deg, #e0c33a 0%, #f7d64a 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "inline",
                    }}
                  >
                    Vic
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#FFFFFF", display: "inline" }}
                  >
                    Down
                  </Box>
                </Typography>
              )}
            </Box>

            {/* Botón del menú móvil */}
            {renderMobileButton()}

            {/* Navegación de escritorio */}
            {renderDesktopNav()}

            {/* Menú móvil */}
            {renderMobileMenu()}
          </Toolbar>
        </AppBar>
      </Slide>

      {/* Snackbar para aviso de idiomas en móvil */}
      <Snackbar
        open={showMobileLanguageNotice}
        autoHideDuration={4000}
        onClose={() => setShowMobileLanguageNotice(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowMobileLanguageNotice(false)}
          severity="info"
          sx={{
            width: "100%",
            boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
            bgcolor: theme.palette.primary.light,
            color: "#fff",
          }}
        >
          {t(
            "header.comingSoonLanguages",
            "Próximamente más idiomas. Estamos trabajando en ello ❤️",
          )}
        </Alert>
      </Snackbar>
    </>
  );
};

Header.displayName = "Header";
export default memo(Header);
